import color from 'styles/themes/wikifactoryNew/styles/colors';

export const DiffStatus = {
  add: '+',
  delete: '-',
  edit: '⇄',
  mergeSplit: '⑂',
};

export const getDiffStatusSymbol = status => {
  return status === DiffStatus.delete
    ? '-'
    : status === DiffStatus.add
    ? '+'
    : status === DiffStatus.mergeSplit
    ? '⑂'
    : status === DiffStatus.edit
    ? '⇄'
    : '';
};

export const getDiffStatusColor = status => {
  return status === DiffStatus.delete
    ? color.wfRed300
    : status === DiffStatus.add
    ? color.wfGreen200
    : status === DiffStatus.mergeSplit
    ? color.wfPurple200
    : status === DiffStatus.edit
    ? color.wfYellow200
    : color.wfWhite;
};

const strokeColorMap = {
  [DiffStatus.delete]: color.wfRed,
  [DiffStatus.add]: color.wfGreen,
  [DiffStatus.mergeSplit]: color.wfPurple,
  [DiffStatus.edit]: color.wfYellow,
};

export const getStrokeColor = status => {
  return strokeColorMap[status] || color.wfBlack;
};

export const getDiffStatusDarkColor = status => {
  return status === DiffStatus.delete
    ? color.wfRed
    : status === DiffStatus.add
    ? color.wfGreen
    : status === DiffStatus.mergeSplit
    ? color.wfPurple
    : status === DiffStatus.edit
    ? color.wfYellow
    : color.wfWhite;
};

// wrapper object for wasm [DiffGroup]
export class DiffGroup {
  seeds = new Map();
  seedsRev = new Map();
  changes = [];
  edits = new Map();
  mergesWith = [];
  selfIx = -1;
  isAdd = false;
  mergedTo = -1;

  constructor(diffTool, ix) {
    const diffGroupObj = diffTool?.GetDiffGroup(ix);
    if (!diffGroupObj) return;

    this.selfIx = diffGroupObj.selfIx;
    this.isAdd = diffGroupObj.isAdd;
    this.mergedTo = diffGroupObj.mergedTo;

    // get seeds
    const seedsKeys = diffGroupObj.seeds.keys();
    for (let i = 0; i < seedsKeys.size(); i++) {
      const refFace = seedsKeys.get(i);
      const diffFace = diffGroupObj.seeds.get(refFace);
      this.seeds.set(refFace, diffFace);
    }

    const seedsRevKeys = diffGroupObj.seedsRev.keys();
    for (let i = 0; i < seedsRevKeys.size(); i++) {
      const refFace = seedsRevKeys.get(i);
      const diffFace = diffGroupObj.seedsRev.get(refFace);
      this.seedsRev.set(refFace, diffFace);
    }

    // get changes
    this.changes.push([]);
    for (let i = 0; i < diffGroupObj.changes.size(); i++) {
      const faceIx = diffGroupObj.changes.get(i);
      this.changes[0].push(faceIx);
    }

    // get edits
    const editKeys = diffGroupObj.edits.keys();
    for (let i = 0; i < editKeys.size(); i++) {
      const refFaceIx = editKeys.get(i);
      const faceIx = diffGroupObj.edits.get(refFaceIx);
      this.edits.set(refFaceIx, faceIx);
    }

    // get merged data
    const nMerged = diffGroupObj.mergesWith.size();
    for (let i = 0; i < nMerged; i++) {
      const mergedIx = diffGroupObj.mergesWith.get(i);
      this.mergesWith.push(mergedIx);

      const mergedDiffGroup = new DiffGroup(diffTool, mergedIx);
      if (mergedDiffGroup) {
        // merge seeds
        this.seeds = new Map([...this.seeds, ...mergedDiffGroup.seeds]);
        this.seedsRev = new Map([
          ...this.seedsRev,
          ...mergedDiffGroup.seedsRev,
        ]);
        // stack changes
        this.changes.push([]);
        for (let j = 0; j < mergedDiffGroup.changes.length; j++) {
          this.changes[1] = [...mergedDiffGroup.changes[0]];
        }
        // merge edits
        this.edits = new Map([...this.edits, ...mergedDiffGroup.edits]);
      }
    }

    // group summary counts
    this.editCount = this.edits.size;
    this.splitAddCount = this.seedsRev.size;
    this.mergeDeleteCount = this.seeds.size;

    this.addCount = 0;
    this.delCount = 0;
    let n = 0;
    for (let j = 0; j < this.changes.length; j++) {
      n += this.changes[j].length;
    }
    if (this.isAdd) this.addCount = n;
    else this.delCount = n;
  }
}

export class BodyDiff {
  groups = [];

  constructor(diffTool) {
    if (!diffTool) return;

    const { isAssemblyDiff, isMeshDiff, hasResult, diffGroupCount } = diffTool;

    if (!hasResult || isAssemblyDiff || isMeshDiff) {
      return;
    }

    for (let i = 0; i < diffGroupCount; i++) {
      const diffGroupObj = diffTool?.GetDiffGroup(i);
      if (diffGroupObj?.mergedTo === -1) {
        // not a merged group...
        const diffGroup = new DiffGroup(diffTool, i);
        this.groups.push(diffGroup);
      }
    }
  }

  get groupCount() {
    return this.groups.length;
  }
}

export class MeshDiff {
  result = null;

  constructor(diffTool) {
    if (!diffTool) return;

    const { isAssemblyDiff, isMeshDiff, hasResult } = diffTool;

    if (!hasResult || isAssemblyDiff || !isMeshDiff) {
      return;
    }

    const resObj = diffTool.GetMeshResult();
    this.result = {
      refPtCount: resObj.refPtCount,
      diffPtCount: resObj.diffPtCount,
      added: resObj.added.size(),
      deleted: resObj.deleted.size(),

      refTriCount: resObj.refTriCount,
      diffTriCount: resObj.diffTriCount,
      addedTris: resObj.addedTris.size(),
      deletedTris: resObj.deletedTris.size(),
      addedArea: resObj.addedArea,
      deletedArea: resObj.deletedArea,
    };
  }
}

export class AssemblyDiff {
  added = [];
  deleted = [];
  transformed = new Map();

  constructor(diffTool) {
    if (!diffTool) return;

    const { isAssemblyDiff, hasResult } = diffTool;
    const split = diffTool.GetResult();

    if (!hasResult || !isAssemblyDiff) {
      return;
    }

    for (let i = 0; i < split.added.size(); i++) {
      const instanceId = split.added.get(i);
      this.added.push(instanceId);
    }

    for (let i = 0; i < split.deleted.size(); i++) {
      const instanceId = split.deleted.get(i);
      this.deleted.push(instanceId);
    }

    const keys = split.transformed?.keys();
    // eslint-disable-next-line no-restricted-syntax
    for (let i = 0; i < keys.size(); i++) {
      const instanceId = keys.get(i);
      const instanceIdDiff = split.transformed.get(instanceId);
      this.transformed.set(instanceId, instanceIdDiff);
    }
  }
}
